import { useFocusHeading } from '@windrop/ui';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { RouteTabs, type RouteTab } from './Tabs';

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  breadcrumb?: Crumb[];
  /** Small uppercase label above the title (object kind, e.g. "Claim"). */
  kicker?: ReactNode;
  /** Primary and secondary actions (Buttons / links). The primary action goes last. */
  actions?: ReactNode;
  /** Badge or status next to the title. */
  meta?: ReactNode;
  /** Section tabs rendered under the header (routes). */
  tabs?: RouteTab[];
  /** Base path the tabs are relative to. */
  tabsBase?: string;
  /** Extra content between description and tabs (filters, status strip). */
  children?: ReactNode;
}

/** Page header with breadcrumb, title, meta, actions, description and optional route tabs. The h1 receives focus on
 * route change so screen readers land on it (no visible ring: it is not interactive, §13.5). */
export function PageHeader({
  title,
  description,
  breadcrumb,
  kicker,
  actions,
  meta,
  tabs,
  tabsBase = '',
  children,
}: PageHeaderProps) {
  const headingRef = useFocusHeading<HTMLHeadingElement>(typeof title === 'string' ? title : undefined);
  return (
    <header className="wdc-page-header">
      {breadcrumb && breadcrumb.length ? (
        <nav aria-label="Breadcrumb" className="wdc-breadcrumb">
          <ol>
            {breadcrumb.map((c, i) => (
              <li key={`${c.label}-${i}`} aria-current={i === breadcrumb.length - 1 ? 'page' : undefined}>
                {c.to && i < breadcrumb.length - 1 ? (
                  <Link to={c.to}>{c.label}</Link>
                ) : (
                  <span>{c.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}
      {kicker ? <span className="wdc-page-header__kicker">{kicker}</span> : null}
      <div className="wdc-page-header__row">
        <div className="wdc-page-header__title">
          <h1 ref={headingRef} tabIndex={-1} className="wd-display-sm">
            {title}
          </h1>
          {meta}
        </div>
        {actions ? <div className="wd-btn-group wdc-page-header__actions">{actions}</div> : null}
      </div>
      {description ? <p className="wd-muted wdc-page-header__desc">{description}</p> : null}
      {children}
      {tabs && tabs.length ? (
        <div className="wdc-page-header__tabs">
          <RouteTabs base={tabsBase} tabs={tabs} />
        </div>
      ) : null}
    </header>
  );
}
